/**
 * Restock dialog (formerly "Add stock", renamed 2026-08 refresh) — load/restock blanks the way staff
 * read a garment tag (brand/style → color → per size), never a SKU. Lives at the inventory-hub level
 * (roadmap Phase 1c) so it's reachable from every tab, not just On-hand. Style search + color picker
 * are the calculator's own components; the size×quantity grid resolves each row's canonical SKU
 * behind the scenes and reuses POST /inventory/:sku/add-stock per filled size, in the selected mode
 * (Add = increment, Set = anchor to a physical count). A live "New on hand" column previews the
 * result so add-vs-set can't be misread.
 *
 * Set is a SHELF count since pulled-blanks v2 (B2): the figure typed per size is what is on the shelf,
 * and the server adds the stacks the pull station recorded as pulled and not yet drawn (`book = shelf +
 * Σ out`, the same branch as `/count`, with the same refusals). The dialog reads the count sheet when
 * Set is chosen (fresh every time — its `asOf` is what the post echoes) and previews `typed + pulled`;
 * a zero is allowed on this basis (nothing left on the shelf, the book becomes the pulled stacks). On an
 * old server or a degraded read, Set falls back to the legacy anchor and says so. Each size's refusal is
 * shown in the server's words with its own answers; the token per SKU replays on a retry.
 *
 * This dialog stays the ONLY safe door for creating a brand-new blank: /add-stock resolves the SKU
 * against S&S before any write (an unknown SKU 404s with nothing written) and re-keys the ledger to
 * the canonical style SKU — plain Adjust/Count would mint an undeletable typo-SKU row. It is NOT for
 * S&S deliveries: those go through Receiving (PO tie-back, claims, invoice dedupe), and a box entered
 * here double-counts if Receiving later confirms it — hence the nudge in the description.
 */
import type { BlankPulledSheet } from '@fsp/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import {
  NO_ROSE_ANSWERS,
  parseRefusal,
  pulledSeenOf,
  type Refusal,
  type RoseAnswers,
  withRoseAnswer,
} from './count-shelf-lib';
import { invalidateInventory, pickSizeRows } from './inventory-lib';
import { ColorCombobox } from './screen-print-quote/color-combobox';
import { StyleCombobox, type StylePick } from './screen-print-quote/style-combobox';

type BlankOnHand = components['schemas']['BlankOnHand'];

/** The count sheet as Set reads it: per-SKU pulled stacks and the server time they were derived at. */
type ShelfRead =
  | { legacy: true; why: 'old-server' | 'degraded' }
  | {
      legacy: false;
      asOf: string;
      bySku: Map<string, { now: number; sheets: BlankPulledSheet[] }>;
    };

export function AddStockDialog({
  open,
  onClose,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  existing: BlankOnHand[];
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] sm:max-w-xl">
        {open && <AddStockBody existing={existing} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function AddStockBody({ existing, onClose }: { existing: BlankOnHand[]; onClose: () => void }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [stylePick, setStylePick] = useState<StylePick | null>(null);
  const [color, setColor] = useState('');
  const [qtyBySize, setQtyBySize] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  // 'add' = increment on-hand (restock / local purchase); 'set' = anchor to a physical count.
  // Defaults to 'add' — the everyday action and what the "Add stock" button implies.
  const [mode, setMode] = useState<'add' | 'set'>('add');
  // One idempotency token per SKU, minted on first submit and REUSED across a partial-failure retry
  // (each failed size re-POSTs the same key → server dedupes, never double-adds). Cleared for sizes
  // that landed, and wholesale when the blank/color/mode context changes (a genuinely new action).
  const tokensBySku = useRef<Map<string, string>>(new Map());
  const resetTokens = () => tokensBySku.current.clear();
  /** sizeName → the refusal its last post met (shelf-basis Set), with the answers given since. */
  const [refusals, setRefusals] = useState<Record<string, Refusal>>({});
  const [answersBySku, setAnswersBySku] = useState<Record<string, RoseAnswers>>({});

  const style = stylePick?.style ?? '';

  const variantsQuery = useQuery({
    queryKey: ['style-variants', style],
    enabled: style.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/garments/by-style/{style}/variants', {
        params: { path: { style } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Could not load sizes.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  // Set reads the count sheet fresh on every open of the mode: the pulled stacks per size and the
  // `asOf` the post echoes. Under the frozen `count-shelf` root (exempt from the change feed): this
  // read is short-lived and its own, never the count page's snapshot.
  const shelf = useQuery({
    queryKey: ['count-shelf', 'restock'],
    enabled: mode === 'set',
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<ShelfRead> => {
      const res = await client.GET('/inventory/count-sheet');
      if (res.response?.status === 404) return { legacy: true, why: 'old-server' };
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Could not read the book.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      const d = res.data.data;
      if (d.degraded) return { legacy: true, why: 'degraded' };
      return {
        legacy: false,
        asOf: d.asOf,
        bySku: new Map(
          d.rows.map((r) => [r.ssSku, { now: r.pulledQty ?? 0, sheets: r.pulledSheets ?? [] }]),
        ),
      };
    },
  });
  const shelfBasis = mode === 'set' && shelf.data !== undefined && !shelf.data.legacy;
  const pulledOf = (sku: string) =>
    shelf.data && !shelf.data.legacy ? shelf.data.bySku.get(sku) : undefined;

  // Color-match is case/whitespace-insensitive (see pickSizeRows) so sizes never silently drop when
  // S&S spells a color inconsistently across its sizes; one row per size, size-ordered.
  const sizesForColor = pickSizeRows(variantsQuery.data?.variants ?? [], color);

  const onHandOf = (sku: string) => existing.find((b) => b.ssSku === sku)?.onHand;

  // A zero is a real entry on ONE path only: a shelf-basis Set (nothing left on the shelf; the book
  // becomes the pulled stacks). Everywhere else a 0 / blank is "not entered", as before.
  const filledRows = sizesForColor
    .map((v) => ({ v, n: Number(qtyBySize[v.sizeName]) }))
    .filter(
      ({ v, n }) =>
        (qtyBySize[v.sizeName] ?? '').trim() !== '' &&
        Number.isInteger(n) &&
        (n > 0 || (shelfBasis && n === 0)),
    );

  const addAll = useMutation({
    mutationFn: async () => {
      const rows = filledRows.map(({ v, n }) => ({
        sku: v.sku,
        sizeName: v.sizeName,
        qty: Math.trunc(n),
      }));
      const read = shelf.data;
      const results = await Promise.all(
        rows.map(async (r): Promise<{ ok: true } | { ok: false; refusal: Refusal }> => {
          // Reuse this SKU's token if it already has one (a retry) so the replay dedupes; else mint one.
          let tok = tokensBySku.current.get(r.sku);
          if (!tok) {
            tok = crypto.randomUUID();
            tokensBySku.current.set(r.sku, tok);
          }
          const a = answersBySku[r.sku] ?? NO_ROSE_ANSWERS;
          const body =
            mode === 'set' && read && !read.legacy
              ? {
                  quantity: r.qty,
                  mode,
                  // The shelf basis: the typed figure plus advisory ids — never a cached quantity.
                  basis: 'shelf' as const,
                  asOf: read.asOf,
                  pulledSeen: pulledSeenOf(read.bySku.get(r.sku)?.sheets ?? []),
                  pulledExclude: a.exclude.length > 0 ? [...a.exclude] : undefined,
                  confirmPulled:
                    a.confirm.length > 0 ? a.confirm.map((c) => ({ ...c })) : undefined,
                  note: note.trim() || undefined,
                  clientToken: tok,
                }
              : { quantity: r.qty, mode, note: note.trim() || undefined, clientToken: tok };
          try {
            const res = await client.POST('/inventory/{ssSku}/add-stock', {
              params: { path: { ssSku: r.sku } },
              body,
            });
            if (res.error) {
              return { ok: false, refusal: parseRefusal(res.error.error, res.response?.status) };
            }
            if (!res.data?.ok) throw new Error('failed');
            return { ok: true };
          } catch (e) {
            return {
              ok: false,
              refusal: {
                kind: 'other',
                title: 'Not saved',
                detail: e instanceof Error ? e.message : 'failed',
              },
            };
          }
        }),
      );
      const failed = rows.filter((_, i) => results[i]?.ok === false).map((r) => r.sizeName);
      const reasons: Record<string, Refusal> = {};
      rows.forEach((r, i) => {
        const res = results[i];
        if (res && !res.ok) reasons[r.sizeName] = res.refusal;
      });
      // Drop tokens for sizes that LANDED (a future add of that size is a new action); KEEP tokens for
      // failed sizes so the retry replays the same key.
      const failedSet = new Set(failed);
      for (const r of rows) if (!failedSet.has(r.sizeName)) tokensBySku.current.delete(r.sku);
      return { added: rows.length - failed.length, failed, total: rows.length, reasons };
    },
    onSuccess: (summary) => {
      if (summary.added > 0) invalidateInventory(qc);
      setRefusals(summary.reasons);
      // Drop the counts that landed; keep ONLY the failed sizes so a retry re-sends just those (not
      // the ones already recorded) and the "their counts are kept" message is accurate.
      setQtyBySize((prev) =>
        summary.failed.length === 0
          ? {}
          : Object.fromEntries(
              Object.entries(prev).filter(([size]) => summary.failed.includes(size)),
            ),
      );
    },
  });

  const resetAll = () => {
    setQtyBySize({});
    setRefusals({});
    setAnswersBySku({});
    addAll.reset();
    resetTokens();
  };

  const answerRose = (sku: string, sheetId: string, out: number, kind: 'counted' | 'press') => {
    setAnswersBySku((m) => ({
      ...m,
      [sku]: withRoseAnswer(m[sku] ?? NO_ROSE_ANSWERS, sheetId, out, kind),
    }));
  };
  /** The answer already given for a refusal's sheet, if any — the cell acknowledges it and the next Set
   *  carries it (one answer per sheet). */
  const answerFor = (sku: string, sheetId: string): 'counted' | 'press' | undefined => {
    const a = answersBySku[sku];
    if (!a) return undefined;
    if (a.exclude.includes(sheetId)) return 'counted';
    if (a.confirm.some((c) => c.batchId === sheetId)) return 'press';
    return undefined;
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Restock</DialogTitle>
        <DialogDescription>
          Pick the blank and color, then enter how many per size — no SKU needed.{' '}
          <span className="font-medium text-foreground">
            Came in an S&amp;S box? Use Receiving instead.
          </span>
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-1">
        <Label className="text-xs font-medium text-muted-foreground">Blank</Label>
        <StyleCombobox
          value={style}
          onPick={(pick) => {
            setStylePick(pick);
            setColor('');
            resetAll();
          }}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs font-medium text-muted-foreground">Color</Label>
        <ColorCombobox
          style={style}
          value={color}
          onPick={(c) => {
            setColor(c);
            resetAll();
          }}
        />
      </div>

      {color && (
        <div className="space-y-3 rounded-md border p-3">
          {/* Mode toggle — the number you enter either ADDS to or SETS on-hand. The "New" column
              previews the result live so it can never be misread. */}
          <div className="flex items-center gap-1 rounded-md bg-muted p-0.5 text-xs font-medium">
            <button
              type="button"
              onClick={() => {
                setMode('add');
                resetAll();
              }}
              className={
                mode === 'add'
                  ? 'flex-1 rounded bg-background px-2 py-1 shadow-sm'
                  : 'flex-1 rounded px-2 py-1 text-muted-foreground'
              }
            >
              Add to on-hand
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('set');
                resetAll();
              }}
              className={
                mode === 'set'
                  ? 'flex-1 rounded bg-background px-2 py-1 shadow-sm'
                  : 'flex-1 rounded px-2 py-1 text-muted-foreground'
              }
            >
              Set to count
            </button>
          </div>
          <Label className="text-xs font-medium text-muted-foreground">
            {mode === 'add' ? (
              <>
                How many came in? Each amount is <span className="font-medium">added</span> to that
                size.
              </>
            ) : shelfBasis ? (
              <>
                Set is a <span className="font-medium">shelf count</span>: blanks recorded as pulled
                are added in.
              </>
            ) : (
              <>
                Physical count — this <span className="font-medium">replaces</span> each size's on
                hand.
              </>
            )}
          </Label>
          {mode === 'set' && shelf.isPending && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3 animate-spin" /> Reading the book…
            </p>
          )}
          {mode === 'set' && shelf.isError && (
            <p className="text-xs text-destructive">
              Couldn't read the book — Set is unavailable until it loads.
            </p>
          )}
          {mode === 'set' && shelf.data?.legacy && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {shelf.data.why === 'old-server'
                ? 'Server not yet updated for shelf counts — Set replaces each size: count pulled stacks in.'
                : "The pulled stacks couldn't be read — Set replaces each size: count pulled stacks in."}
            </p>
          )}

          {variantsQuery.isPending ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading sizes…
            </div>
          ) : variantsQuery.isError ? (
            <p className="text-sm text-destructive">
              Couldn't load sizes.{' '}
              {variantsQuery.error instanceof Error ? variantsQuery.error.message : ''}
            </p>
          ) : sizesForColor.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sizes for this color.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="py-1 text-left font-medium">Size</th>
                  <th className="py-1 text-right font-medium">On hand now</th>
                  <th className="py-1 text-right font-medium">
                    {mode === 'add' ? 'Add' : shelfBasis ? 'On the shelf' : 'Set to'}
                  </th>
                  <th className="py-1 text-right font-medium">New on hand</th>
                </tr>
              </thead>
              <tbody>
                {sizesForColor.map((v) => {
                  const cur = onHandOf(v.sku);
                  const raw = (qtyBySize[v.sizeName] ?? '').trim();
                  const n = Number(raw);
                  const pulledNow = shelfBasis ? (pulledOf(v.sku)?.now ?? 0) : 0;
                  // Only an entry that will be submitted previews a "New on hand": a 0 / blank shows
                  // "—" everywhere except the shelf-basis Set, where 0 is a real figure (the book
                  // becomes the pulled stacks).
                  const entered =
                    raw !== '' && Number.isInteger(n) && (n > 0 || (shelfBasis && n === 0))
                      ? n
                      : null;
                  // Live preview of the resulting on-hand: add → current + entered; set → entered
                  // (+ the pulled stacks on the shelf basis).
                  const next =
                    entered === null
                      ? null
                      : mode === 'add'
                        ? (cur ?? 0) + entered
                        : entered + pulledNow;
                  const refusal = refusals[v.sizeName];
                  return (
                    <tr key={v.sku} className="border-t">
                      <td className="py-1.5 font-medium">{v.sizeName}</td>
                      <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                        {cur == null ? '—' : cur}
                        {shelfBasis && pulledNow > 0 && (
                          <span className="block text-[11px]">{pulledNow} pulled</span>
                        )}
                      </td>
                      <td className="py-1.5 text-right">
                        <Input
                          type="number"
                          inputMode="numeric"
                          step="1"
                          min="0"
                          aria-label={`${mode === 'add' ? 'Add to' : 'Set'} size ${v.sizeName}`}
                          className="ml-auto h-8 w-24 text-right"
                          value={qtyBySize[v.sizeName] ?? ''}
                          onChange={(e) => {
                            setQtyBySize((prev) => ({ ...prev, [v.sizeName]: e.target.value }));
                            setRefusals((prev) => {
                              if (!(v.sizeName in prev)) return prev;
                              const { [v.sizeName]: _dropped, ...rest } = prev;
                              return rest;
                            });
                          }}
                        />
                      </td>
                      <td className="py-1.5 text-right tabular-nums font-medium">
                        {next === null ? '—' : next}
                        {refusal && (
                          <RefusalCell
                            refusal={refusal}
                            busy={addAll.isPending}
                            answered={
                              refusal.kind === 'rose' && refusal.sheets[0]
                                ? answerFor(v.sku, refusal.sheets[0].batchId)
                                : undefined
                            }
                            onAnswer={(sheetId, out, kind) => answerRose(v.sku, sheetId, out, kind)}
                            onReload={() => {
                              setRefusals((prev) => {
                                const { [v.sizeName]: _dropped, ...rest } = prev;
                                return rest;
                              });
                              void shelf.refetch();
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div className="min-w-[10rem] flex-1 space-y-1">
              <Label htmlFor="add-note" className="text-xs">
                Note (optional)
              </Label>
              <Input
                id="add-note"
                placeholder={mode === 'add' ? 'added stock' : 'opening stock'}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <Button
              onClick={() => addAll.mutate()}
              disabled={
                filledRows.length === 0 ||
                addAll.isPending ||
                (mode === 'set' && (shelf.isPending || shelf.isError))
              }
            >
              {addAll.isPending && <Loader2 className="size-4 animate-spin" />}
              {mode === 'add' ? 'Add' : 'Set'}
              {filledRows.length > 0 ? ` ${filledRows.length}` : ''}
            </Button>
          </div>

          {addAll.isError && (
            <p className="text-xs text-destructive">
              {addAll.error instanceof Error ? addAll.error.message : 'Save failed.'}
            </p>
          )}
          {/* Success banner hides once new amounts are entered (filledRows > 0) so it can't read
              stale; the partial-failure banner stays because those failed sizes are kept for retry. */}
          {addAll.isSuccess && addAll.data.failed.length === 0 && filledRows.length === 0 && (
            <p className="text-xs text-emerald-700 dark:text-emerald-400">
              {mode === 'add' ? 'Added to' : 'Set'} {addAll.data.added} size
              {addAll.data.added === 1 ? '' : 's'} of {color} {style}. Add another, or press Done.
            </p>
          )}
          {addAll.isSuccess && addAll.data.failed.length > 0 && (
            <p className="text-xs text-destructive">
              {mode === 'add' ? 'Added' : 'Set'} {addAll.data.added}/{addAll.data.total}. Not saved:{' '}
              {addAll.data.failed.join(', ')} — each says why above; their amounts are kept.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onClose}>
          Done
        </Button>
      </div>
    </>
  );
}

/** One size's refusal under its preview: the server's words, and the answer it asks for. */
function RefusalCell({
  refusal,
  busy,
  answered,
  onAnswer,
  onReload,
}: {
  refusal: Refusal;
  busy: boolean;
  /** The answer already given for this refusal's sheet — shown instead of the two buttons. */
  answered?: 'counted' | 'press';
  onAnswer: (sheetId: string, out: number, kind: 'counted' | 'press') => void;
  onReload: () => void;
}) {
  const first = refusal.kind === 'rose' ? refusal.sheets[0] : undefined;
  return (
    <div
      className="mt-1 space-y-1 text-left text-[11px] font-normal text-destructive"
      data-testid="size-refusal"
      data-kind={refusal.kind}
    >
      <p>
        <span className="font-medium">{refusal.title}.</span> {refusal.detail}
      </p>
      {refusal.kind === 'rose' && first && answered && (
        <p className="text-foreground" data-testid="size-answered">
          Will resend as:{' '}
          {answered === 'counted' ? 'counted on the shelf' : 'at the press (added back)'} — press
          Set.
        </p>
      )}
      {refusal.kind === 'rose' && first && !answered && (
        <div className="flex flex-wrap gap-1">
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[11px]"
            disabled={busy}
            onClick={() => onAnswer(first.batchId, first.out, 'counted')}
          >
            Yes, I counted them
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[11px]"
            disabled={busy}
            onClick={() => onAnswer(first.batchId, first.out, 'press')}
          >
            No, they were at the press
          </Button>
        </div>
      )}
      {(refusal.kind === 'changed' || refusal.kind === 'moved' || refusal.kind === 'stale') && (
        <Button
          size="sm"
          variant="outline"
          className="h-6 px-2 text-[11px]"
          disabled={busy}
          onClick={onReload}
        >
          Re-read the book
        </Button>
      )}
    </div>
  );
}
